import type { z } from 'zod';
import { ApiError } from '@/shared/types/api.types';

/**
 * Validates a payload at the transport boundary.
 *
 * The Core service is a separate deployable, so its response shape is an assumption rather
 * than a guarantee. Parsing here means a contract drift surfaces as one clear error naming
 * the endpoint, instead of an undefined property thrown from somewhere inside a component
 * tree several frames away from the cause.
 *
 * Generic over the schema rather than over its result, so that a schema which decodes as
 * it validates — `.default()` for protobuf's unset fields, `.transform()` for numeric
 * enums — yields its decoded type. Naming the result type instead would collapse the two
 * and hand callers the raw wire shape.
 */
export function parseOrThrow<TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  payload: unknown,
  description: string
): z.output<TSchema> {
  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new ApiError('server', `The server returned an unexpected ${description}.`, {
      cause: result.error,
    });
  }
  return result.data as z.output<TSchema>;
}
